import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { DS } from '../theme';

interface Project {
  id: number;
  name: string;
  user_id: number;
  dataset_count: number;
  created_at: string;
}

interface DashboardPageProps {
  username?: string;
  isGuest?: boolean;
  projects: Project[];
  isLoading: boolean;
  onCreateProject: () => void;
  onOpenProject: (project: Project) => void;
  onNavigate: (page: 'projects' | 'chat' | 'analysis') => void;
  onOpenSample?: () => void;
  onSignUp?: () => void;
}

// ── Section header with trailing line ──────────────────────────────────────────
const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '16px' }}>
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: DS.textFaint,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', backgroundColor: DS.border }} />
  </Box>
);

// ── Stat card ──────────────────────────────────────────────────────────────────
const StatCard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  isLoading?: boolean;
}> = ({ label, value, sub, isLoading }) => (
  <Box
    sx={{
      flex: 1,
      px: '22px',
      pt: '22px',
      pb: '20px',
      backgroundColor: DS.bg2,
      border: `1px solid ${DS.border}`,
      borderRadius: '12px',
      transition: 'border-color 0.15s, background-color 0.15s',
      '&:hover': {
        borderColor: DS.borderStrong,
        backgroundColor: DS.bg3,
      },
    }}
  >
    <Typography
      sx={{ fontSize: '12px', fontWeight: 500, color: DS.textFaint, mb: '8px' }}
    >
      {label}
    </Typography>
    {isLoading ? (
      <Skeleton width={60} height={36} sx={{ bgcolor: DS.bg3 }} />
    ) : (
      <Typography
        sx={{
          fontSize: '36px',
          fontWeight: 600,
          color: DS.text,
          lineHeight: 1,
          letterSpacing: '-1.5px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
    )}
    {sub && (
      <Typography sx={{ fontSize: '11px', color: DS.textFaint, mt: '6px' }}>
        {sub}
      </Typography>
    )}
  </Box>
);

// ── How-it-works card ──────────────────────────────────────────────────────────
const HowItWorksCard: React.FC<{
  step: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
}> = ({ step, icon, title, desc }) => (
  <Box
    sx={{
      flex: 1,
      p: '28px 24px',
      backgroundColor: DS.bg2,
      transition: 'background-color 0.15s',
      '&:hover': { backgroundColor: DS.bg3 },
    }}
  >
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: 600,
        color: DS.accent,
        letterSpacing: '0.06em',
        mb: '12px',
      }}
    >
      {step}
    </Typography>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '10px',
        backgroundColor: DS.accentDim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: DS.accent,
        mb: '16px',
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontSize: '15px',
        fontWeight: 600,
        color: DS.text,
        letterSpacing: '-0.3px',
        mb: '8px',
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: '13px',
        color: DS.textMuted,
        lineHeight: 1.55,
      }}
    >
      {desc}
    </Typography>
  </Box>
);

// ── Sample Dataset amber banner ────────────────────────────────────────────────
const SampleBanner: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      px: '22px',
      py: '18px',
      backgroundColor: DS.amberDim,
      border: `1px solid ${DS.amberBorder}`,
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      mb: '40px',
      '&:hover': {
        backgroundColor: 'rgba(214,157,74,0.18)',
        borderColor: 'rgba(214,157,74,0.3)',
      },
    }}
  >
    {/* Icon */}
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '10px',
        backgroundColor: DS.amber,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <TrendingUpIcon sx={{ fontSize: 20, color: '#1a0e00' }} />
    </Box>

    {/* Text */}
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '4px' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: DS.text }}>
          Sample Dataset
        </Typography>
        <Box
          sx={{
            fontSize: '10px',
            fontWeight: 700,
            textTransform: 'uppercase',
            backgroundColor: DS.amber,
            color: '#1a0e00',
            px: '7px',
            py: '2px',
            borderRadius: '4px',
            lineHeight: 1.4,
          }}
        >
          Try it
        </Box>
      </Box>
      <Typography sx={{ fontSize: '12.5px', color: DS.textMuted }}>
        1,000 rows of sales data — explore charts, filters, and AI without uploading anything
      </Typography>
    </Box>

    {/* Arrow */}
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: '8px',
        border: `1px solid ${DS.borderStrong}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: DS.textMuted,
        flexShrink: 0,
        transition: 'background-color 0.15s',
        '.MuiBox-root:hover &': { backgroundColor: DS.bg3 },
      }}
    >
      <ArrowForwardIcon sx={{ fontSize: 15 }} />
    </Box>
  </Box>
);

// ── Arrow button (reusable) ────────────────────────────────────────────────────
const ArrowBtn: React.FC = () => (
  <Box
    sx={{
      width: 30,
      height: 30,
      borderRadius: '8px',
      border: `1px solid ${DS.borderStrong}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: DS.textMuted,
      flexShrink: 0,
      transition: 'background-color 0.15s, border-color 0.15s',
    }}
  >
    <ArrowForwardIcon sx={{ fontSize: 15 }} />
  </Box>
);

// ── Main component ─────────────────────────────────────────────────────────────
const DashboardPage: React.FC<DashboardPageProps> = ({
  username,
  isGuest = false,
  projects: initialProjects,
  isLoading,
  onCreateProject,
  onOpenProject,
  onNavigate,
  onOpenSample,
  onSignUp,
}) => {
  const [projects, setProjects] = useState(initialProjects);
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);

  React.useEffect(() => { setProjects(initialProjects); }, [initialProjects]);

  const handleDeleteProject = async (
    e: React.MouseEvent,
    projectId: number,
    projectName: string
  ) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${projectName}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/projects/${projectId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch {}
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).toUpperCase();

  const totalDatasets = projects.reduce((s, p) => s + (p.dataset_count || 0), 0);

  const displayName = isGuest
    ? 'Guest'
    : username
    ? (() => {
        const n = username.split(' ')[0];
        return n.charAt(0).toUpperCase() + n.slice(1);
      })()
    : 'Guest';

  const howItWorksSteps = [
    {
      step: '01',
      icon: <CloudUploadIcon sx={{ fontSize: 18 }} />,
      title: 'Upload a CSV',
      desc: 'Any size, any format. We handle encoding automatically.',
    },
    {
      step: '02',
      icon: <BarChartIcon sx={{ fontSize: 18 }} />,
      title: 'Explore instantly',
      desc: 'See stats, charts and column breakdowns in one click.',
    },
    {
      step: '03',
      icon: <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />,
      title: 'Ask questions',
      desc: 'Type in plain English. AI generates and runs the query for you.',
    },
  ];

  return (
    <Box
      sx={{
        p: '40px 48px 60px',
        maxWidth: 1000,
        width: '100%',
      }}
    >
      {/* ── Sign-up dialog (guest only) ── */}
      <Dialog
        open={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: DS.bg3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <FolderOpenIcon sx={{ fontSize: 22, color: DS.textMuted }} />
          </Box>
          <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, color: DS.text, mb: 1 }}>
            Create a free account
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: DS.textMuted, lineHeight: 1.6, mb: 3 }}>
            You're exploring as a guest. Sign up for free to create projects, upload your own
            datasets, and save your work.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setShowSignUpPrompt(false);
              onSignUp?.();
            }}
            sx={{ mb: 1.5, py: 1.25, fontSize: '0.9375rem' }}
          >
            Sign Up — It's Free
          </Button>
          <Button
            variant="text"
            fullWidth
            onClick={() => setShowSignUpPrompt(false)}
            sx={{ fontSize: '0.875rem' }}
          >
            Continue as Guest
          </Button>
        </DialogContent>
      </Dialog>

      {/* ── Greeting ── */}
      <Box sx={{ mb: '40px' }}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: DS.textFaint,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            mb: '6px',
          }}
        >
          {today}
        </Typography>
        <Typography
          sx={{
            fontSize: '32px',
            fontWeight: 600,
            color: DS.text,
            letterSpacing: '-0.8px',
            lineHeight: 1.2,
          }}
        >
          {greeting()}, {displayName}.
        </Typography>
      </Box>

      {/* ── How DataScope Works ── */}
      <Box sx={{ mb: '32px' }}>
        <SectionHeader label="How DataScope Works" />

        {/* 3-col grid with 1px gap trick */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1px',
            backgroundColor: DS.border,
            borderRadius: '14px',
            overflow: 'hidden',
          }}
        >
          {howItWorksSteps.map(s => (
            <HowItWorksCard key={s.step} {...s} />
          ))}
        </Box>
      </Box>

      {/* ── Sample Dataset Banner ── */}
      {onOpenSample && <SampleBanner onClick={onOpenSample} />}

      {/* ── Projects ── */}
      <Box>
        <SectionHeader label="Projects" />

        {/* Stats grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            mb: '16px',
          }}
        >
          <StatCard
            label="Total Projects"
            value={isGuest ? 0 : isLoading ? '—' : projects.length}
            isLoading={!isGuest && isLoading}
          />
          <StatCard
            label="Total Datasets"
            value={isGuest ? 0 : isLoading ? '—' : totalDatasets}
            isLoading={!isGuest && isLoading}
          />
          <StatCard
            label="Ready to Analyze"
            value={isGuest ? 0 : isLoading ? '—' : totalDatasets}
            sub="datasets uploaded"
            isLoading={!isGuest && isLoading}
          />
        </Box>

        {/* New Project card */}
        <Box
          onClick={isGuest ? () => setShowSignUpPrompt(true) : onCreateProject}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            px: '22px',
            py: '18px',
            backgroundColor: DS.bg2,
            border: `1px solid ${DS.border}`,
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'border-color 0.15s, background-color 0.15s',
            mb: '12px',
            '&:hover': {
              borderColor: 'rgba(107,94,248,0.3)',
              backgroundColor: DS.bg3,
              '& .new-project-arrow': {
                backgroundColor: DS.bg4,
              },
            },
          }}
        >
          {/* Plus icon */}
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '9px',
              backgroundColor: DS.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              flexShrink: 0,
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </Box>

          {/* Text */}
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: '14px', fontWeight: 600, color: DS.text, mb: '2px' }}
            >
              New Project
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: DS.textMuted }}>
              {isGuest
                ? 'Sign up to create projects and upload your own data'
                : 'Create a project to organise your datasets'}
            </Typography>
          </Box>

          {/* Arrow */}
          <Box
            className="new-project-arrow"
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              border: `1px solid ${DS.borderStrong}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: DS.textMuted,
              flexShrink: 0,
              transition: 'background-color 0.15s',
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 15 }} />
          </Box>
        </Box>

        {/* Recent projects list (logged-in only) */}
        {!isGuest && projects.length > 0 && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: '10px',
                mt: '24px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: DS.textFaint,
                }}
              >
                Recent
              </Typography>
              <Button
                variant="text"
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 12 }} />}
                onClick={() => onNavigate('projects')}
                sx={{ color: DS.textFaint, fontSize: '0.75rem', minWidth: 0, p: '2px 6px' }}
              >
                View all
              </Button>
            </Box>

            <Box
              sx={{
                backgroundColor: DS.bg2,
                border: `1px solid ${DS.border}`,
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              {isLoading
                ? [1, 2, 3].map(i => (
                    <Box
                      key={i}
                      sx={{ px: '22px', py: '16px', borderBottom: `1px solid ${DS.border}` }}
                    >
                      <Skeleton width={200} height={16} sx={{ mb: 0.5, bgcolor: DS.bg3 }} />
                      <Skeleton width={100} height={12} sx={{ bgcolor: DS.bg3 }} />
                    </Box>
                  ))
                : projects.slice(0, 5).map((project, index) => (
                    <Box
                      key={project.id}
                      onClick={() => onOpenProject(project)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: '22px',
                        py: '14px',
                        borderBottom:
                          index < Math.min(projects.length, 5) - 1
                            ? `1px solid ${DS.border}`
                            : 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.12s',
                        '&:hover': { backgroundColor: DS.bg3 },
                        '&:hover .proj-delete': { opacity: 1 },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            backgroundColor: DS.bg3,
                            borderRadius: '7px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FolderOpenIcon sx={{ fontSize: 14, color: DS.textMuted }} />
                        </Box>
                        <Box>
                          <Typography
                            sx={{ fontSize: '14px', fontWeight: 500, color: DS.text }}
                          >
                            {project.name}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: DS.textFaint }}>
                            {project.dataset_count} dataset
                            {project.dataset_count !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Tooltip title="Delete project">
                          <IconButton
                            className="proj-delete"
                            size="small"
                            onClick={e =>
                              handleDeleteProject(e, project.id, project.name)
                            }
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.15s',
                              color: DS.textMuted,
                              '&:hover': {
                                color: '#EF4444',
                                bgcolor: 'rgba(239,68,68,0.1)',
                              },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <ArrowForwardIcon sx={{ fontSize: 15, color: DS.textFaint }} />
                      </Box>
                    </Box>
                  ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DashboardPage;
